import React, { useEffect, useState } from "react";
import "../CommonTable/CommonTable.scss";
import RoundCheck from "../../../assets/images/vactor/green-check-round.svg";
import FreeIcon from "../../../assets/images/vactor/free-icon.svg";
import DotsMenu from "../../../assets/images/vactor/dot-menu.svg";

import {
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { useAppDispatch } from "../../../Store/store";
import { deletePlan, viewPlans } from "../../../Services/admin.service";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../AllButtons/AllButtons";

interface Data {
  id: number;
  planName: string;
  tagline: string;
  token: number;
  amountInUSD: number;
  planStatus: string;
  action: string;
}

type Order = "asc" | "desc";

const descendingComparator = <T,>(a: T, b: T, orderBy: keyof T) => {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
};

const getComparator = <Key extends keyof any>(
  order: Order,
  orderBy: Key
): ((
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number) =>
  order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);

const TableForPlan = () => {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("planName");
  const [selected, setSelected] = React.useState<readonly number[]>([]);

  const [moreMenu, setMoreMenu] = React.useState<null | HTMLElement>(null);
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);

  const open = Boolean(moreMenu);
  const handleClickMoreMenu = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    setMoreMenu(event.currentTarget);
    setSelectedRowId(id);
  };
  const handleCloseMoreMenu = () => {
    setMoreMenu(null);
    setSelectedRowId(null);
  };

  const dispatch = useAppDispatch();
  const [plans, setPlans] = useState<any>();

  useEffect(() => {
    dispatch(viewPlans())
      .then((response) => {
        setPlans(response.payload.data);
      })
      .catch((error) => {
        console.error("Error fetching plans:", error);
      });
  }, [dispatch]);

  const rows: Data[] =
    plans?.map((plan: any) => ({
      id: plan.id,
      planName: plan.name || "Unnamed Plan",
      tagline: plan.plan_description || "No description",
      token: plan.tokens,
      amountInUSD: plan.amount,
      planStatus: "Active", // Adjust if API provides status
      action: "",
    })) || [];

  const handleRequestSort = (property: keyof Data) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleViewSpecificPlan = async (planId: number | null) => {
    if (planId !== null) {
      try {  
        localStorage.setItem('specificPlan', 'true');
        localStorage.setItem('planId', planId.toString());
        navigate('/admin/create_plan'); 
      } catch (error) {
        console.error("Failed to fetch specific plan", error);
      }
    }
  };
  

  // const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   if (event.target.checked) {
  //     setSelected(rows.map((n) => n.id));
  //   } else {
  //     setSelected([]);
  //   }
  // };

  const handleDeletePlan = (planId: number | null) => {
    if (planId !== null) {
      dispatch(deletePlan(planId))
        .then(() => {
          setPlans((prevPlans: any[]) =>
            prevPlans.filter((plan) => plan.id !== planId)
          );
          handleCloseMoreMenu();
        })
        .catch((error) => {
          console.error("Failed to delete plan:", error);
        });
    }
  };

  const handleClick = (id: number) => {
    setSelected((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    );
  };

  const visibleRows = React.useMemo(
    () => [...rows].sort(getComparator(order, orderBy)),
    [order, orderBy, rows]
  );

  const navigate = useNavigate();

  return (
    <>
      <div className="common-table-wrapper">
        <div className="user-management-table">
          <div className="common-table-filter-block">
            <PrimaryButton
              onClick={() => {
                localStorage.setItem('specificPlan','false');
                navigate("/admin/create_plan");
              }}
            >
              Create Plan
            </PrimaryButton>
          </div>

          <TableContainer>
            <Table aria-label="basic table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "planName"}
                      direction={orderBy === "planName" ? order : "asc"}
                      onClick={() => handleRequestSort("planName")}
                    >
                      Plan Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === "tagline"}
                      direction={orderBy === "tagline" ? order : "asc"}
                      onClick={() => handleRequestSort("tagline")}
                    >
                      Tagline
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === "token"}
                      direction={orderBy === "token" ? order : "asc"}
                      onClick={() => handleRequestSort("token")}
                    >
                      Token
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === "amountInUSD"}
                      direction={orderBy === "amountInUSD" ? order : "asc"}
                      onClick={() => handleRequestSort("amountInUSD")}
                    >
                      Amount in USD
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Plan Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => handleClick(row.id)}
                    role="checkbox"
                    aria-checked={selected.includes(row.id)}
                    selected={selected.includes(row.id)}
                  >
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>{row.planName}</TableCell>
                    <TableCell align="left">{row.tagline}</TableCell>
                    <TableCell align="center">
                      {row.token.toLocaleString()==="-1" ? "Unlimited" : row.token.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">${row.amountInUSD}</TableCell>
                    <TableCell align="center">
                      <div>
                        <div className="subscription-badge">
                          <img src={RoundCheck} alt="" /> Active
                        </div>
                        <div className="free-badge d-none">
                          <img src={FreeIcon} alt="" /> Inactive
                        </div>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <div>
                        <IconButton
                          edge="end"
                          aria-label="menu"
                          id="moremunu-button"
                          aria-controls={open ? "basic-moremenu" : undefined}
                          aria-haspopup="true"
                          aria-expanded={open ? "true" : undefined}
                          onClick={(event) => handleClickMoreMenu(event, row.id)}
                          sx={{ minWidth: "34px" }}
                        >
                          <img src={DotsMenu} alt="more menu" />
                        </IconButton>
                        <Menu
                          className="moremenu-popup"
                          id="basic-moremenu"
                          anchorEl={moreMenu}
                          open={open}
                          onClose={handleCloseMoreMenu}
                          MenuListProps={{
                            "aria-labelledby": "moremunu-button",
                          }}
                        >
                          <MenuItem onClick={()=>{handleViewSpecificPlan(selectedRowId)}}>
                            <span>
                              <svg
                                width="18"
                                height="17"
                                viewBox="0 0 20 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M9.9998 0.9C14.8526 0.9 18.89 4.392 19.7369 9C18.8909 13.608 14.8526 17.1 9.9998 17.1C5.147 17.1 1.1096 13.608 0.262695 9C1.1087 4.392 5.147 0.9 9.9998 0.9ZM9.9998 15.3C11.8353 15.2996 13.6163 14.6761 15.0514 13.5317C16.4864 12.3872 17.4904 10.7894 17.8991 9C17.4889 7.21197 16.4842 5.616 15.0494 4.47302C13.6145 3.33004 11.8343 2.70768 9.9998 2.70768C8.16532 2.70768 6.38512 3.33004 4.95023 4.47302C3.51535 5.616 2.51069 7.21197 2.1005 9C2.50918 10.7894 3.5132 12.3872 4.94823 13.5317C6.38325 14.6761 8.16427 15.2996 9.9998 15.3V15.3ZM9.9998 13.05C8.92567 13.05 7.89554 12.6233 7.13601 11.8638C6.37649 11.1043 5.9498 10.0741 5.9498 9C5.9498 7.92587 6.37649 6.89574 7.13601 6.13622C7.89554 5.3767 8.92567 4.95 9.9998 4.95C11.0739 4.95 12.1041 5.3767 12.8636 6.13622C13.6231 6.89574 14.0498 7.92587 14.0498 9C14.0498 10.0741 13.6231 11.1043 12.8636 11.8638C12.1041 12.6233 11.0739 13.05 9.9998 13.05ZM9.9998 11.25C10.5965 11.25 11.1688 11.0129 11.5908 10.591C12.0127 10.169 12.2498 9.59674 12.2498 9C12.2498 8.40326 12.0127 7.83097 11.5908 7.40901C11.1688 6.98705 10.5965 6.75 9.9998 6.75C9.40306 6.75 8.83076 6.98705 8.40881 7.40901C7.98685 7.83097 7.7498 8.40326 7.7498 9C7.7498 9.59674 7.98685 10.169 8.40881 10.591C8.83076 11.0129 9.40306 11.25 9.9998 11.25Z"
                                  fill="#525866"
                                />
                              </svg>
                            </span>
                            View
                          </MenuItem>
                          {/* <MenuItem 
                          onClick={() => handleDeletePlan(selectedRowId)} 
                          disabled={visibleRows?.length === 1}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                verticalAlign: "middle",
                                // marginRight: 8,
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 20 18"
                                fill="none"
                              >
                                <path
                                  d="M13.75 5.5H17.5V7H16V16.75C16 16.9489 15.921 17.1397 15.7803 17.2803C15.6397 17.421 15.4489 17.5 15.25 17.5H4.75C4.55109 17.5 4.36032 17.421 4.21967 17.2803C4.07902 17.1397 4 16.9489 4 16.75V7H2.5V5.5H6.25V3.25C6.25 3.05109 6.32902 2.86032 6.46967 2.71967C6.61032 2.57902 6.80109 2.5 7 2.5H13C13.1989 2.5 13.3897 2.57902 13.5303 2.71967C13.671 2.86032 13.75 3.05109 13.75 3.25V5.5ZM14.5 7H5.5V16H14.5V7ZM7.75 9.25H9.25V13.75H7.75V9.25ZM10.75 9.25H12.25V13.75H10.75V9.25ZM7.75 4V5.5H12.25V4H7.75Z"
                                  fill="#DF1C41"
                                />
                              </svg>
                            </span>
                            Delete
                          </MenuItem> */}
                        </Menu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </>
  );
};

export default TableForPlan;
